import {useEffect, useRef, useState} from 'react'

const TENCENT_CAPTCHA_CDN = 'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js'

interface TencentCaptchaWidgetProps {
  appId: string
  onVerify: (ticket: string, randstr: string) => void
  onReady?: (reload: () => void) => void
}

export function TencentCaptchaWidget({appId, onVerify, onReady}: TencentCaptchaWidgetProps) {
  const [sdkReady, setSdkReady] = useState(!!(window as any).TencentCaptcha)
  const containerRef = useRef<HTMLDivElement>(null)
  const captchaRef = useRef<any>(null)
  const initialised = useRef(false)
  const onVerifyRef = useRef(onVerify)
  const onReadyRef = useRef(onReady)

  useEffect(() => {
    onVerifyRef.current = onVerify
  }, [onVerify])

  useEffect(() => {
    onReadyRef.current = onReady
  }, [onReady])

  // Load SDK script
  useEffect(() => {
    if ((window as any).TencentCaptcha) {
      setSdkReady(true)
      return
    }

    const existing = document.querySelector(`script[src="${TENCENT_CAPTCHA_CDN}"]`)
    if (existing) {
      existing.addEventListener('load', () => setSdkReady(true))
      return
    }

    const script = document.createElement('script')
    script.src = TENCENT_CAPTCHA_CDN
    script.async = true
    script.onload = () => setSdkReady(true)
    document.head.appendChild(script)
  }, [])

  // Initialise embedded widget once SDK is ready
  useEffect(() => {
    if (!sdkReady || initialised.current || !containerRef.current) return

    const TencentCaptcha = (window as any).TencentCaptcha
    if (!TencentCaptcha) return

    try {
      captchaRef.current = new TencentCaptcha(
        containerRef.current,
        appId,
        (res: {ret: number; ticket: string; randstr: string}) => {
          if (res.ret === 0 && res.ticket && res.randstr) {
            onVerifyRef.current(res.ticket, res.randstr)
          }
        },
        {type: 'embed'}
      )
      captchaRef.current.show()
      initialised.current = true

      // Expose reload so parent can reset the widget for a fresh ticket
      if (onReadyRef.current) {
        onReadyRef.current(() => {
          captchaRef.current?.reload?.()
        })
      }
    } catch (e) {
      console.error('[TencentCaptcha] init failed', e)
    }
  }, [sdkReady, appId])

  return (
    <div className='d-flex justify-content-center mb-5'>
      <div ref={containerRef} style={{width: '300px', minHeight: '52px'}} />
    </div>
  )
}
